import { isTrackingSchema } from "./schemaTypes";

/**
 * applyThemeCore — pure theme-transition computation.
 *
 * Single source of truth for what MainPage.applyTheme(theme) writes to state
 * when the user switches section/theme. Extracted verbatim from MainPage so
 * desktop and (later) mobile share one implementation with no reimplementation.
 *
 * PURE: no setState, no `this`, no randomness, no side-effects. Given a theme
 * and the current selectedVariable it returns the exact, deterministic state
 * payload desktop applyTheme passes to setState — plus `defaultVar`, a
 * NON-STATE field the caller needs to fire its post-setState callbacks.
 *
 * Dependency-injected (themeConfigs / themeOptions / buildLabelsAndColors) so
 * this module imports nothing app-specific. Desktop passes its existing
 * THEME_configs / THEME_OPTIONS / buildLabelsAndColors unchanged.
 *
 * EXCLUDED on purpose (stay in the desktop wrapper, see MainPage.applyTheme):
 *   - the random secondary ActionTargetVariable (nondeterministic; not part of
 *     the setState payload — only an argument to a post-setState callback)
 *   - the setState call itself and the three callbacks fired afterwards
 *     (handleVariableChange, two handleActionTargetVariableChange)
 *
 * Return keys match desktop setState keys EXACTLY (theme_colours, not
 * themeColours) so the caller can spread the result straight into setState.
 *
 * @param {object}   args
 * @param {string}   args.theme               target theme slug ("pol", "phrma", ...)
 * @param {string?}  args.currentVariable      this.state.selectedVariable at call time
 * @param {Array}    args.themeConfigs         THEME_configs: [{ value, config(), colours(), defaultVar }]
 * @param {Array}    args.themeOptions         THEME_OPTIONS: [{ value, label, ... }]
 * @param {Function} args.buildLabelsAndColors (groupsById, theme_colours) -> { labels, colors }
 * @returns {object} exact setState payload + defaultVar (non-state)
 */
export function applyThemeCore({
  theme,
  currentVariable,
  themeConfigs,
  themeOptions,
  buildLabelsAndColors,
}) {
  const themeEntry = themeConfigs.find(t => t.value === theme);
  const { groupsById } = themeEntry.config();
  const theme_colours = themeEntry.colours();
  const { labels, colors } = buildLabelsAndColors(groupsById, theme_colours);
  const sectiontitle = themeOptions.find(t => t.value === theme).label;
  const variableSchema = groupsById[themeEntry.defaultVar]?.schema;
  const waves = Array.isArray(groupsById[themeEntry.defaultVar]?.waves)
    ? [...groupsById[themeEntry.defaultVar].waves].sort((a, b) => b - a)
    : [];
  const latestWave = waves[0] ?? null;

  return {
    selectedSection: theme,
    sectionTitle: sectiontitle,
    groupsById,
    theme_colours,
    colorLabels: labels,
    colorMap: colors,
    selectedVariable:
      (Object.keys(groupsById).includes(currentVariable) && !groupsById[currentVariable]?.hidden)
        ? currentVariable
        : themeEntry.defaultVar,
    geoLevel: "states",
    ActionMode: "explore",
    waveOptions: isTrackingSchema(variableSchema) ? waves : [],
    waveSelect: isTrackingSchema(variableSchema) ? latestWave : null,
    compareWaves: false,
    compareKey: "__top__",

    // non-state — caller uses it for callback #1 (val: defaultVar) and to seed randomKey
    defaultVar: themeEntry.defaultVar,
  };
}
